import React, { CSSProperties, ReactNode } from 'react'
import { BRAND_NAME, BRAND_SEAL } from './brand'
import { ICP_FILING_NUMBER, ICP_FILING_URL, LICENSE_URL, NOTICE_URL, POLICE_FILING_ICON_URL, POLICE_FILING_NUMBER, POLICE_FILING_URL, UPSTREAM_COPYRIGHT, UPSTREAM_REPO } from './siteLinks'
import { ThemeContextValue, uiPrimitives, useThemedStyles } from './theme'

/**
 * 站脚 —— 版权与来源署名，常驻内容页。
 *
 * 版式照 `_v2-designs/jiangnan-qinan.html:524` 的 `footer`（朱砂小印 + 一行小字），
 * 内容照 Apache-2.0 的归属义务：本项目是 `Sparks NMN` 的衍生作品，署名必须留给原作者。
 * 完整的四条义务与落点见 `siteLinks.ts` 顶部。
 *
 * ⚠️ **不要接进编辑器 / 分享页**（`#edit` / `#share`）。那两页的 `PageHeader.outer` 是
 *    `minHeight:100vh` 的纵向 flex，`inner` 与 `editorWrap` 都是 `flex:1` ——
 *    谱面本身要吃掉所有余量（`PageHeader.tsx` 里那句注释）。在这里多塞一行，
 *    画布就会矮一行，而且窄屏下是「每滚动一次就少一行」的那种矮。
 *
 * ⚠️ 印章用 `BRAND_SEAL` 这一个字，别在这里拼站名：毛笔体是**手写死字符集**的子集字体，
 *    写进去的字不在 `subset-fonts.mjs` 的 `BRUSH_CHARSET` 里就会掉回系统宋体。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	foot: {
		marginTop: 46,
		paddingTop: 18,
		borderTop: `1px solid ${t.palette.line}`,
		// 两行结构：第一行 = 印章 + 署名（横向排），第二行 = 免责声明。
		// 两行各自水平居中；行内折行也居中（textAlign 继承到子项）。
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		gap: 10,
		fontSize: 12.5,
		color: t.palette.ink3,
	},

	/** 第一行容器：印章与署名横排，整体作为一行居中 */
	firstLine: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 10,
	},

	/** 朱砂小印 —— 尺寸照设计稿的 `.seal-sm`（22px），比顶栏的 30px 再小一号 */
	seal: {
		flex: 'none',
		/** 行高 1.9 × 12.5px ≈ 23.8px 的行框里，22px 方章居中要往下挪约 1px */
		marginTop: 1,
		width: 22,
		height: 22,
		display: 'grid',
		placeItems: 'center',
		background: t.palette.cinnabar,
		color: '#fff',
		borderRadius: t.radius.sm,
		fontFamily: t.fonts.brush,
		fontSize: 12,
		lineHeight: 1,
		userSelect: 'none',
	},

	/**
	 * 整段署名走**一个** flex 项、内部按文本排 —— 不是为了省事，是排版要求：
	 * 分隔点用 flex 子项排的话，换行会发生在 `gap` 处，于是「漫漫弹 ·」的
	 * 那个点会孤零零留在行尾、或者「· 改编自…」的点跑到行首。按文本排就只在
	 * 空格处断行，点始终贴着它前面的词。
	 */
	text: {
		lineHeight: 1.9,
		// 容器已是 maxWidth 适应内容的 flex 列；显式 inline-block 保证折行时
		// 以自身文本盒居中（宽屏一行放下时看不出差别，窄屏折行时是居中的）
		display: 'inline-block',
	},

	link: {
		color: t.palette.ink3,
		textDecoration: 'none',
		borderBottom: `1px solid ${t.palette.line2}`,
		paddingBottom: 1,
		whiteSpace: 'nowrap',
		'&:hover': {
			color: t.palette.celadon,
			borderBottomColor: t.palette.celadon,
		},
	},

	/**
	 * 免责声明 —— 独占一行（纵向 flex 的第二个子项），比署名再小一号再淡一档。
	 * 不并进 `text`：它不是站点署名的一部分，语气也不同（对外声明 vs 版权信息）。
	 */
	disclaimer: {
		fontSize: 11.5,
		lineHeight: 1.7,
		color: t.palette.ink3,
		opacity: 0.85,
	},

	/**
	 * 备案行 —— 第三行，法规要求的落点：公安备案（公网安备）与 ICP 备案同排，
	 * 按惯例公安在前、两者中间一个分隔点。
	 *
	 * 两项各自的法规落点（号 + 官网链接 + 徽章图标）见 `siteLinks.ts` 的
	 * `POLICE_FILING_*` 与 `ICP_FILING_*`。与免责声明同字号同色（不另起一档）：
	 * 它们是合规信息不是内容，存在感越低越好，但必须可点、必须指向官网。
	 *
	 * `inline-flex` 让两项作为**一行整体**居中；`flexWrap` 允许窄屏在两项
	 * 之间折行 —— `link` 自身是 nowrap 的，备案号不会被从中间截断。
	 */
	filing: {
		fontSize: 11.5,
		lineHeight: 1.7,
		color: t.palette.ink3,
		opacity: 0.85,
		display: 'inline-flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		justifyContent: 'center',
	},

	/**
	 * 公安徽章图标 —— 源图 62×67 竖版（`public/gonganbu.png`），按 18px 高
	 * 等比缩放（宽约 17px）。两个细节：
	 *   · `verticalAlign: 'middle'` —— img 默认按基线排，会比文字下沉一点，
	 *     徽章与号码要视觉居中对齐；
	 *   · 徽章随链接一起带上 `link` 的下边框——官方 badge 本来就是链接的一部分。
	 */
	policeIcon: {
		height: 18,
		width: 'auto',
		verticalAlign: 'middle',
		marginRight: 5,
	},

	/** 两项备案之间的分隔点 —— 比正文再淡一档，不抢合规信息的视线 */
	filingSep: {
		margin: '0 7px',
		opacity: 0.55,
	},
})

const useStyles = () => useThemedStyles(stylesFactory)

export function SiteFooter(props: {
	/**
	 * 站名之后、署名之前的一句自家话。
	 *
	 * 首页原来那行「一个写给自娱自乐的人的记谱工具。」就是从这里进来的 ——
	 * 设计稿只有一个页脚，所以是**扩充**它，不是在它下面再接一个。
	 */
	note?: ReactNode
	/**
	 * 追加样式。用来覆盖 `marginTop` —— 首页那一屏的间距是照设计稿单独调过的
	 * （`HomePage` 原来写 72），而指南页正文列里 46 更贴。
	 */
	style?: CSSProperties
}) {
	const classes = useStyles()
	// `data-site-footer` 是给巡检脚本用的稳定钩子：这个根元素是 `div` 不是 `footer`，
	// 靠文案找（「改编自开源项目」）在别的页面讲到开源时会误判。
	return <div className={classes.foot} data-site-footer="1" style={props.style}>
		{/* 印章 + 站名署名是同一行的内容：包成一个 flex 行项，免责声明才是第二行 */}
		<span className={classes.firstLine}>
			<span className={classes.seal} aria-hidden='true'>{BRAND_SEAL}</span>
			<span className={classes.text}>
				{BRAND_NAME}
				{props.note !== undefined && <> · {props.note}</>}
				{' · 改编自开源项目 '}
				<a className={classes.link} href={UPSTREAM_REPO} target='_blank' rel='noreferrer'>Sparks NMN</a>
				{'（非官方） · Apache-2.0 · '}
				{UPSTREAM_COPYRIGHT}
				{' · '}
				<a className={classes.link} href={LICENSE_URL}>许可证</a>
				{' · '}
				<a className={classes.link} href={NOTICE_URL}>NOTICE</a>
			</span>
		</span>

		<span className={classes.disclaimer}>
			本站曲谱均来自用户上传及互联网收集，仅供学习交流使用，版权归原作者所有；如有侵权请联系
			<a className={classes.link} href='mailto:wy_kim@126.com'>wy_kim@126.com</a>
			，我们将尽快处理。
		</span>

		<span className={classes.filing}>
			{/* 公安备案（徽章 + 号）与 ICP 备案同排，公安在前。
			    徽章 alt 留空：链接文字已完整表达，徽章是法规样式不是信息。 */}
			<a className={classes.link} href={POLICE_FILING_URL} target='_blank' rel='noreferrer'>
				<img className={classes.policeIcon} src={POLICE_FILING_ICON_URL} alt='' />
				{POLICE_FILING_NUMBER}
			</a>
			<span className={classes.filingSep} aria-hidden='true'>·</span>
			<a className={classes.link} href={ICP_FILING_URL} target='_blank' rel='noreferrer'>
				{ICP_FILING_NUMBER}
			</a>
		</span>
	</div>
}

export default SiteFooter
